import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import PageCard from '../../../common/PageCard'
import $t from '../../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import Actions from 'actions'
import { getUrlParams, setUrlParams } from 'utils/useParamUtil'
import {
  isIndividualProvider,
  isAntidoteProvider,
  getProviderSpecialities
} from 'utils/providerSearchUtils'
import { capitalizeFirstLetter, routes } from 'consts'
import { Button } from 'antd'
import ProviderAction from './ProviderAction'
import ProviderTier from '../../../common/ProviderTier/ProviderTier'
import { ProviderPCPTypes } from '../../../../consts/ProviderConsts'
import ProviderDetailsLanguages from './ProviderDetailsLanguages'
import { isNil } from 'ramda'
import { useNativeMessage } from 'hooks'
import { getCopayAndNetworkCopay } from 'utils/hmoUtils'
import providerAvatar from 'resources/generic-doctor.svg'
import buildings from 'resources/buildings.png'
import ProviderCopay from '../ProviderCopay/ProviderCopay'
import './ProviderDetails.css'

export const ProviderDetails = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const {
    selectedProviderDetails,
    providerToken,
    telehealthPcp,
    physicalPcp,
    isChangeTelehealthPcpAvailbale
  } = useSelector(state => state.providers)
  const selectedMember = useSelector(state => state.account?.selectedMember)
  const userDetails = useSelector(state => state.profile?.userDetails)
  const isProviderLoading = useSelector(
    state => state.providers?.isProviderDetailsLoading
  )
  const memberCopay = useSelector(state => state?.nhMember?.memberCopay)

  const { copay } = getCopayAndNetworkCopay({
    planDetailsCopay: memberCopay,
    provider: selectedProviderDetails
  })

  const provider = selectedProviderDetails

  const isProviderIndividual = isIndividualProvider(provider)
  const isProviderAntidote = isAntidoteProvider(provider)

  const shouldShowChangeButton = isProviderAntidote
    ? isChangeTelehealthPcpAvailbale
    : true

  const MAX_AFFILIATIONS_TO_SHOW = 5
  const IndividualHospitalAffiliationsHidden = true // TODO: temp solution, remove this after we implement relvant facilities as hospital affiliations feature
  const shouldShowMoreButton =
    provider?.hospital_affiliations?.length > MAX_AFFILIATIONS_TO_SHOW
  const [showAllHospitalAffiliations, setShowAllHospitalAffiliations] =
    useState(false)

  const { hideBack, changePcp } = getUrlParams()

  // Use native message hook to handle React Native WebView communication
  const { sendMessage } = useNativeMessage({
    sendLoadedMessage: true,
    shouldSendLoaded: hideBack
  })

  const isLocationExist =
    provider?.address || provider?.city || provider?.state || provider?.zipcode
  const phoneNumber = provider?.provider_numbers?.find(
    number => number?.number_type === 'phone'
  )?.number

  const isNotAdmin = !userDetails || selectedMember?.id !== userDetails?.id

  const telehealthPCPAccordingToUser = isNotAdmin
    ? telehealthPcp
    : userDetails?.telehealthPcp
  const physicalPCPAccordingToUser = isNotAdmin
    ? physicalPcp
    : userDetails?.physicalPcp

  // get pcp without the endDate (we can't edit pcp with end date)
  const newestPCP = physicalPCPAccordingToUser?.find(pcp => !pcp?.end_date)

  const isPCP = isProviderAntidote
    ? telehealthPCPAccordingToUser?.id.toString() === provider?.id.toString()
    : newestPCP?.id.toString() === provider?.id.toString()

  useEffect(() => {
    // get the provider info according to id from url params
    dispatch(Actions.createAction(Actions.PROVIDER_DETAILS_REQUESTED, { id }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const onChangeTelehealthPCP = () => {
    sendMessage('disable-back')
    const payload = {
      changePcpType: ProviderPCPTypes.TELEHEALTH,
      antidote: true,
      query: 'PCP',
      changePcp: true
    }
    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        `${routes.SEARCH_PROVIDERS_RESULTS}?${setUrlParams(payload)}`
      )
    )
  }

  const onChangePCP = () => {
    sendMessage('disable-back')
    const payload = {
      changePcpType: ProviderPCPTypes.PHYSICAL,
      query: 'PCP',
      changePcp: true
    }
    dispatch(
      Actions.createAction(
        Actions.NAVIGATED_TO,
        `${routes.SEARCH_PROVIDERS_RESULTS}?${setUrlParams(payload)}`
      )
    )
  }

  return (
    <PageCard
      className='provider-details'
      enableBack={!hideBack}
      loading={isProviderLoading}
    >
      <div
        className={
          hideBack
            ? 'provider-details-sections'
            : 'provider-details-sections with-back-btn'
        }
      >
        <div className='provider-details-left-section provider-details-section'>
          <div className={'provider-details no-border'}>
            <div className='provider-details-row'>
              <img
                src={isProviderIndividual ? providerAvatar : buildings}
                alt='provider'
                className='provider-img'
              />
            </div>
            <div className='provider-name semi-bold'>
              {`${provider?.name} ${
                provider?.prac_category_code
                  ? ', ' + provider?.prac_category_code
                  : ''
              }`}
            </div>
            <div className='provider-desc'>
              {getProviderSpecialities(provider)}
            </div>
            {provider?.tier && (
              <div className='provider-tier'>
                <ProviderTier tier={provider.tier} />
              </div>
            )}
            {isProviderIndividual && provider?.gender && (
              <div className='provider-desc'>
                {$t('providerPage.providerDetails.gender')}:{' '}
                {capitalizeFirstLetter(provider?.gender)}
              </div>
            )}
            {provider?.is_antidote && (
              <div className='highlight-flag'>
                <i className='icon-video-fill' />
                {$t('providerPage.providerDetails.teleOnly')}
              </div>
            )}
          </div>
          {!provider?.is_antidote && isLocationExist && (
            <div className='provider-details'>
              <div className='provider-details-row'>
                <div className='provider-desc black'>{provider?.address}</div>
                {/* <Button type="link">
                {$t('providerPage.providerDetails.otherLocations')}
              </Button> */}
              </div>
              <div className='provider-desc'>
                {[provider?.city, provider?.state, provider?.zipcode]
                  .filter(val => !!val)
                  .join(', ')}
              </div>
            </div>
          )}
          {
            // copay can be 0
            !isNil(copay) && isProviderIndividual && (
              <div className='provider-details'>
                <div className='provider-details-row'>
                  <div className='provider-desc semi-bold'>
                    {$t('providerPage.providerDetails.copay')}
                  </div>
                  <ProviderCopay
                    planDetailsCopay={memberCopay}
                    provider={provider}
                    useDefaultTextAfterCopayPrice={false}
                  />
                </div>
              </div>
            )
          }
          {phoneNumber && (
            <div className='provider-details'>
              <div className='provider-details-row'>
                <div className='provider-desc semi-bold'>
                  {$t('providerPage.providerDetails.phone')}
                </div>
                <div className='provider-desc'>{phoneNumber}</div>
              </div>
            </div>
          )}
          {provider?.practice_restrictions && (
            <div className='provider-details'>
              <div className='provider-details-row'>
                <div className='provider-desc semi-bold'>
                  {$t('providerPage.providerDetails.accepts')}
                </div>
                <div className='provider-desc'>
                  {provider?.practice_restrictions}
                </div>
              </div>
            </div>
          )}
          {isProviderIndividual &&
            !!provider?.telehealth &&
            !!provider?.languages &&
            provider?.languages.length > 0 && (
              <ProviderDetailsLanguages languages={provider?.languages} />
            )}
        </div>

        <div className='provider-details-right-section provider-details-section'>
          {isProviderIndividual &&
            !provider?.telehealth &&
            !!provider?.languages &&
            provider?.languages.length > 0 && (
              <ProviderDetailsLanguages languages={provider?.languages} />
            )}

          {isProviderIndividual && IndividualHospitalAffiliationsHidden ? (
            <div className='provider-details'>
              <div className='provider-desc semi-bold'>
                {$t('providerPage.providerDetails.hospitalAffiliations')}
              </div>
              <div className='provider-desc'>
                {$t('providerPage.dataNotAvailable')}
              </div>
            </div>
          ) : (
            <>
              {isProviderIndividual &&
                provider?.hospital_affiliations?.length === 0 && (
                  <div className='provider-details'>
                    <div className='provider-desc semi-bold'>
                      {$t('providerPage.providerDetails.hospitalAffiliations')}
                    </div>
                    <div className='provider-desc'>
                      {$t('providerPage.dataNotAvailable')}
                    </div>
                  </div>
                )}
              {isProviderIndividual &&
                !!provider?.hospital_affiliations?.length && (
                  <div className='provider-details'>
                    <div className='provider-desc semi-bold'>
                      {$t('providerPage.providerDetails.hospitalAffiliations')}
                    </div>
                    <div className='provider-desc provider-desc-affiliations'>
                      {showAllHospitalAffiliations
                        ? provider?.hospital_affiliations
                            ?.map(item => item?.name)
                            ?.join(',\n')
                        : provider?.hospital_affiliations
                            .slice(0, MAX_AFFILIATIONS_TO_SHOW)
                            ?.map(item => item?.name)
                            ?.join(',\n') ||
                          $t('providerPage.dataNotAvailable')}
                    </div>
                    {shouldShowMoreButton && (
                      <Button
                        onClick={() =>
                          setShowAllHospitalAffiliations(
                            !showAllHospitalAffiliations
                          )
                        }
                        type='link'
                        className='show-more-btn'
                      >
                        {showAllHospitalAffiliations
                          ? $t('providerPage.showLess')
                          : $t('providerPage.showMore')}
                      </Button>
                    )}
                  </div>
                )}
            </>
          )}
          {provider?.school_name && (
            <div className='provider-details'>
              <div className='provider-desc semi-bold'>
                {$t('providerPage.providerDetails.educationTraining')}
              </div>
              <div className='provider-desc'>
                {provider?.school_name || $t('providerPage.dataNotAvailable')}
              </div>
            </div>
          )}
          {provider?.npi && (
            <div className='provider-details'>
              <div className='provider-desc semi-bold'>
                {$t('providerPage.providerDetails.npiNumber')}
              </div>
              <div className='provider-desc'>{provider?.npi}</div>
            </div>
          )}
          {!isProviderIndividual && (
            <>
              <div className='provider-details'>
                <div className='provider-desc semi-bold'>
                  {$t('providerPage.providerDetails.hospitalAccredited')}
                </div>
                <div className='provider-desc'>
                  {isNil(provider?.hospital_accredited)
                    ? $t('providerPage.dataNotAvailable')
                    : provider?.hospital_accredited
                    ? $t('common.yes')
                    : $t('common.no')}
                </div>
              </div>
              <div className='provider-details'>
                {provider?.accrediting_body && (
                  <div
                    className='provider-details-row accreditation-row'
                    style={{
                      cursor: provider?.accreditation_website
                        ? 'pointer'
                        : 'default'
                    }}
                    onClick={() => {
                      if (provider?.accreditation_website) {
                        window.open(provider?.accreditation_website, '_blank')
                      }
                    }}
                  >
                    <div>
                      <div className='provider-desc semi-bold'>
                        {$t('providerPage.providerDetails.accreditingBody')}
                      </div>
                      <div className='provider-desc'>
                        {provider?.accrediting_body ||
                          $t('providerPage.dataNotAvailable')}
                      </div>
                    </div>
                    {provider?.accreditation_website && (
                      <div className='accreditation-icon-container'>
                        <i className='icon-export'></i>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          {isProviderIndividual && (
            <>
              <div className='provider-details'>
                <div className='provider-desc semi-bold'>
                  {$t('providerPage.providerDetails.medicalGroupAffiliations')}
                </div>
                <div className='provider-desc'>
                  {provider?.group_name || $t('providerPage.dataNotAvailable')}
                </div>
              </div>
              <div className='provider-details'>
                <div className='provider-desc semi-bold'>
                  {$t('providerPage.providerDetails.boardCertification')}
                </div>
                <div className='provider-desc'>
                  {provider?.board_certification ||
                    $t('providerPage.dataNotAvailable')}
                </div>
              </div>
            </>
          )}
          {providerToken && changePcp && (
            <ProviderAction
              type='primary'
              className='provider-pcp-action'
              provider={provider}
              isPcpChangeFlow={true}
            />
          )}
          {providerToken && isPCP && !changePcp && (
            <div className='provider-pcp-actions'>
              {isProviderAntidote && (
                <ProviderAction
                  type='primary'
                  className='provider-pcp-action'
                  provider={provider}
                  isPcpChangeFlow={false}
                />
              )}
              {shouldShowChangeButton && (
                <Button
                  type='link'
                  className='provider-pcp-action'
                  onClick={
                    isProviderAntidote ? onChangeTelehealthPCP : onChangePCP
                  }
                >
                  {isProviderAntidote
                    ? $t('profile.careTeam.changeTelehealthPCP')
                    : $t('profile.careTeam.changePCP')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </PageCard>
  )
}

export default ProviderDetails
